import { useEffect, useState } from "react";
import {
  Button,
  CloseButton,
  Combobox,
  Group,
  InputBase,
  Loader,
  Paper,
  Select,
  Text,
  TextInput,
  useCombobox,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import {
  applyCatalogueDefaults,
  getCatalogueDefaults,
  getGuaranteeOptions,
  searchBrandOptions,
} from "../api";

function BrandDefaultInput({ mappingId, value, onChange }) {
  const [listed, setListed] = useState(null);
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState([]);
  const [search, setSearch] = useState(value || "");
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  useEffect(() => {
    setSearch(value || "");
  }, [value]);

  useEffect(() => {
    if (!mappingId) return undefined;
    let cancelled = false;
    searchBrandOptions(mappingId, "")
      .then((data) => {
        if (!cancelled) setListed(!!data?.listed);
      })
      .catch(() => {
        if (!cancelled) setListed(false);
      });
    return () => {
      cancelled = true;
    };
  }, [mappingId]);

  useEffect(() => {
    if (listed !== true) return undefined;
    let cancelled = false;
    setLoading(true);
    searchBrandOptions(mappingId, debouncedSearch)
      .then((data) => {
        if (!cancelled) setOptions(data?.options ?? []);
      })
      .catch(() => {
        if (!cancelled) setOptions([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [listed, mappingId, debouncedSearch]);

  if (listed !== true) {
    return (
      <TextInput
        label="Brand"
        placeholder={listed == null ? "Loading" : "As the marketplace lists it"}
        value={value}
        onChange={(e) => onChange(e.currentTarget.value)}
        disabled={listed == null}
        style={{ flex: "1 1 200px" }}
      />
    );
  }

  return (
    <Combobox
      store={combobox}
      withinPortal
      onOptionSubmit={(label) => {
        onChange(label);
        setSearch(label);
        combobox.closeDropdown();
      }}
    >
      <Combobox.Target>
        <InputBase
          label="Brand"
          placeholder="Search brand"
          value={search}
          style={{ flex: "1 1 200px" }}
          onChange={(event) => {
            combobox.openDropdown();
            combobox.updateSelectedOptionIndex();
            setSearch(event.currentTarget.value);
          }}
          onClick={() => combobox.openDropdown()}
          onFocus={() => combobox.openDropdown()}
          onBlur={() => {
            combobox.closeDropdown();
            setSearch(value || "");
          }}
          rightSectionPointerEvents="all"
          rightSection={
            loading ? (
              <Loader size={18} />
            ) : value ? (
              <CloseButton
                size="sm"
                aria-label="Clear brand"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                  onChange("");
                  setSearch("");
                }}
              />
            ) : (
              <Combobox.Chevron />
            )
          }
        />
      </Combobox.Target>
      <Combobox.Dropdown>
        <Combobox.Options mah={280} style={{ overflowY: "auto" }}>
          {loading ? (
            <Combobox.Empty>Loading…</Combobox.Empty>
          ) : options.length > 0 ? (
            options.map((label) => (
              <Combobox.Option value={label} key={label}>
                {label}
              </Combobox.Option>
            ))
          ) : (
            <Combobox.Empty>No matches</Combobox.Empty>
          )}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
}

function guaranteeChoices(options, current) {
  const list = options ?? [];
  if (current && !list.some((item) => item === current))
    return [current, ...list];
  return list;
}

export default function CatalogueDefaultsBar({ mappingId, onApplied }) {
  const [brand, setBrand] = useState("");
  const [guarantee, setGuarantee] = useState("");
  const [guaranteeOptions, setGuaranteeOptions] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getCatalogueDefaults()
      .then((data) => {
        setBrand(data?.brand ?? "");
        setGuarantee(data?.guarantee ?? "");
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!mappingId) return undefined;
    let cancelled = false;
    getGuaranteeOptions(mappingId)
      .then((data) => {
        if (!cancelled) setGuaranteeOptions(data?.options ?? []);
      })
      .catch(() => {
        if (!cancelled) setGuaranteeOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [mappingId]);

  if (!mappingId) return null;

  const fill = () => {
    setLoading(true);
    applyCatalogueDefaults(mappingId, { brand, guarantee })
      .then((res) => {
        notifications.show({
          title: "Catalogue defaults",
          message:
            res?.filled > 0
              ? `Filled ${res.filled} empty product${res.filled === 1 ? "" : "s"}.`
              : "No empty brand or guarantee fields to fill.",
        });
        onApplied?.();
      })
      .catch((err) => {
        notifications.show({
          color: "red",
          title: "Could not fill defaults",
          message: String(err),
        });
      })
      .finally(() => setLoading(false));
  };

  return (
    <Paper withBorder p="sm" mt="sm" radius="md">
      <Text size="sm" fw={500}>
        Catalogue defaults
      </Text>
      <Text size="xs" c="dimmed" mb="xs">
        Set once. Fills empty brand and guarantee on this file. Does not
        overwrite values already set.
      </Text>
      <Group align="flex-end" gap="sm" wrap="wrap">
        <BrandDefaultInput
          mappingId={mappingId}
          value={brand}
          onChange={setBrand}
        />
        {guaranteeOptions == null || guaranteeOptions.length > 0 ? (
          <Select
            label="Guarantee"
            placeholder="Select guarantee"
            searchable
            data={guaranteeChoices(guaranteeOptions, guarantee)}
            value={guarantee || null}
            onChange={(value) => setGuarantee(value ?? "")}
            disabled={guaranteeOptions == null}
            style={{ flex: "1 1 200px" }}
          />
        ) : (
          <TextInput
            label="Guarantee"
            placeholder="e.g. Statutory"
            value={guarantee}
            onChange={(e) => setGuarantee(e.currentTarget.value)}
            style={{ flex: "1 1 200px" }}
          />
        )}
        <Button
          onClick={fill}
          loading={loading}
          disabled={!brand.trim() && !guarantee.trim()}
        >
          Fill empty products
        </Button>
      </Group>
    </Paper>
  );
}
